import {
  useRentalManagerGuestSupportWebApiHandler_submitRequest,
  useRentalManagerUserSupportWebApiHandler_submitRequest,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import type {
  RentalManagerSupportHelpCategory,
  RentalManagerSupportHelpTopic,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

export type SubmitSupportRequestParams = {
  categoryKey: string;
  topicKey: string;
  topicDescription: string;
  description: string;
  email: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  listingAddress: string;
  listingAlias: string | null | undefined;
  unit: string | undefined;
};

export type SubmitSupportRequestCallbacks = {
  onSuccess?: () => void;
  onError?: (errorMessage: string) => void;
};

export function useSubmitSupportRequest(loggedIn: boolean) {
  const guestMutation = useRentalManagerGuestSupportWebApiHandler_submitRequest();
  const userMutation = useRentalManagerUserSupportWebApiHandler_submitRequest();

  const submit = (params: SubmitSupportRequestParams, callbacks: SubmitSupportRequestCallbacks) => {
    const { onSuccess, onError } = callbacks;

    if (!loggedIn) {
      guestMutation.mutate(
        {
          postData: {
            category: params.categoryKey as RentalManagerSupportHelpCategory,
            topic: params.topicKey as RentalManagerSupportHelpTopic,
            description: params.description,
            email: params.email,
            firstName: params.firstName,
            lastName: params.lastName,
            listingAddress: params.listingAddress || undefined,
            listingAlias: params.listingAlias ?? undefined,
            unit: params.unit,
          },
        },
        {
          onSuccess,
          onError: (err: unknown) => onError?.(DatarouterError.getDisplayMessage(err)),
        },
      );
    } else {
      userMutation.mutate(
        {
          postData: {
            helpTopicKey: params.topicKey,
            helpTopicString: params.topicDescription,
            description: params.description,
            email: params.email ?? '',
            preferredEmail: params.email ?? '',
            listingAddressFreeForm: params.listingAddress,
            aliases: params.listingAlias ? [params.listingAlias] : [],
            userTypeKey: '',
            userTypeString: '',
          },
        },
        {
          onSuccess,
          onError: (err: unknown) => onError?.(DatarouterError.getDisplayMessage(err)),
        },
      );
    }
  };

  const isPending = guestMutation.isPending || userMutation.isPending;
  const isSuccess = guestMutation.isSuccess || userMutation.isSuccess;
  const isError = guestMutation.isError || userMutation.isError;

  const reset = () => {
    guestMutation.reset();
    userMutation.reset();
  };

  return { submit, isPending, isSuccess, isError, reset };
}
